import IconInfoOutline from "@common/components/icons/IconInfoOutline";
import {
  Divider,
  Flex,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { ReactElement } from "react";
import { usePlan } from "@stores/plans/hooks";
import { isYearlyPlan } from "@common/utils/plan";
import { formatQuantity, formatPrice } from "@common/utils/string";
import { useAddons } from "@stores/addons/hooks";
import { ICheckoutTotalProps } from "./types";
import { buildPlanAlias } from "@stores/plans/utils";

export default function CheckoutTotal({
  instanceSetupData,
  payingNow,
  mt,
}: ICheckoutTotalProps): ReactElement {
  const theme = useMantineTheme();

  const plan = usePlan({
    alias: buildPlanAlias({
      basePlanAlias: instanceSetupData.planAlias!,
      isYearly: instanceSetupData.isYearly,
      isSelfHosted: instanceSetupData.isSelfHosted,
      isDev: instanceSetupData.isDev,
    }),
  })!;

  // add-ons price
  const yearly = isYearlyPlan(plan.alias);
  const [storageAddOn] = useAddons({ yearly, dwh: true });

  // billing frequency
  const billingFrequencyText = yearly ? "year" : "month";

  // total
  const storagePrice = instanceSetupData.dwhPrepaidUnits
    ? storageAddOn.default_price_per_unit * instanceSetupData.dwhPrepaidUnits
    : 0;
  const totalPrice = plan.price + storagePrice;

  return (
    <>
      <Divider color="gray.2" mt={mt} />

      {(!payingNow || instanceSetupData.dwhPrepaidUnits) && (
        <Stack data-testid="CheckoutTotal_rows" spacing="xs" mt="sm">
          {!payingNow && (
            <Flex
              align="center"
              direction="row"
              justify="space-between"
              w="100%"
            >
              <Text color="gray.4" fz="xs">
                {instanceSetupData.dwhPrepaidUnits
                  ? plan.description
                  : "Total after 14-day free trial"}
              </Text>
              <Tooltip
                arrowSize={theme.spacing.xs}
                label={
                  <Text fz="xs">
                    <b>
                      {formatPrice(plan.price)}/{billingFrequencyText}
                    </b>{" "}
                    + tax
                    <br />
                    <b>First {plan.users_included} users included</b>,
                    <br />
                    {formatPrice(plan.per_user_price)}/{billingFrequencyText}{" "}
                    additional user
                  </Text>
                }
                withArrow
                withinPortal
              >
                <Flex align="center" direction="row" justify="center">
                  <IconInfoOutline color={theme.colors.gray[4]} size={14} />
                  <Text color="gray.4" fz="xs" ml={theme.spacing.xs / 2}>
                    {formatPrice(plan.price)}/{billingFrequencyText}
                  </Text>
                </Flex>
              </Tooltip>
            </Flex>
          )}

          {instanceSetupData.dwhPrepaidUnits && (
            <>
              <Flex
                align="center"
                direction="row"
                justify="space-between"
                w="100%"
              >
                <Text color="gray.4" fz="xs">
                  Storage ({formatQuantity(instanceSetupData.dwhPrepaidUnits)}{" "}
                  rows)
                </Text>
                <Text color="gray.4" fz="xs" ml={theme.spacing.xs / 2}>
                  {formatPrice(storagePrice)}/{billingFrequencyText}
                </Text>
              </Flex>

              <Flex
                align="center"
                direction="row"
                justify="space-between"
                w="100%"
              >
                <Text color="gray.4" fz="xs">
                  Total after 14-day free trial
                </Text>
                <Text color="gray.4" fz="xs" ml={theme.spacing.xs / 2}>
                  {formatPrice(totalPrice)}/{billingFrequencyText}
                </Text>
              </Flex>
            </>
          )}
        </Stack>
      )}

      <Flex
        data-testid="CheckoutTotal"
        align="center"
        direction="row"
        gap="xl"
        justify="space-between"
        mt="xs"
        w="100%"
      >
        <Text c="gray.5" fw={700}>
          Total due today
        </Text>
        <Text c="gray.5" fw={700}>
          {payingNow
            ? `${formatPrice(totalPrice)}/${billingFrequencyText}`
            : "$0.00"}
        </Text>
      </Flex>
    </>
  );
}
